import { Form } from 'formik';
import { assign, keys, pick } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  BankSelect,
  CloseFormikDialogResult,
  DialogProps,
  FormControls,
  FormikDialog,
  FormikTextField,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { BankAliasDto } from 'types';

type Values = BankAliasDto;

type Props = DialogProps<CloseFormikDialogResult<Values>>;

export const BankAliasDetailsDialog: React.FC<Props> = ({
  open,
  data,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.bank_aliases',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const title = useMemo(
    () => (data ? t('title.edit') : t('title.create')),
    [t, data],
  );

  const getInitialValues = useCallback(
    () => ({
      alias: '',
      bankId: '',
    }),
    [],
  );

  const [initialValues, setInitialValues] = useState(getInitialValues());

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        alias: Yup.string().required(tCommon('errors.required')),
        bankId: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const handleClose = useCallback(
    (data: CloseFormikDialogResult<Values>) => {
      if (!data.ok) {
        data.data?.formikHelpers?.resetForm();
      }
      onClose(data);
    },
    [onClose],
  );

  useEffect(() => {
    const _initialValues = getInitialValues();
    setInitialValues(assign(_initialValues, pick(data, keys(_initialValues))));
  }, [data, getInitialValues]);

  return (
    <FormikDialog
      dialogProps={{
        open,
        title,
        onClose: handleClose,
        maxWidth: 'sm',
        dialogProps: {
          open,
          PaperProps: {
            sx: {
              minHeight: {
                sm: 460,
              },
            },
          },
        },
      }}
      formikProps={{
        validationSchema,
        validateOnMount: true,
        initialValues,
      }}
    >
      {() => (
        <Form data-testid="bank-alias-dialog">
          <FormControls>
            <FormikTextField
              label={t('fields.alias')}
              name="alias"
              required
              data-testid="bank-alias-alias"
            />
            <BankSelect
              name="bankId"
              label={t('fields.bank')}
              required
              data-testid="bank-alias-bank"
            />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
